/* eslint-disable react/prop-types */
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import { cn, formatter } from "@/lib/utils";
import { FcCancel } from "react-icons/fc";
import moment from "moment";
import parse from "html-react-parser";
import { FaDownload, FaShare } from "react-icons/fa6";
import { MdSupportAgent } from "react-icons/md";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import axios from "axios";
import { useState } from "react";
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";

const TransactionDialog = ({ currentTransaction, setCurrentTransaction }) => {
  let theme = window.localStorage.getItem("theme") || "light";
  const [isLoading, setIsLoading] = useState(false);
  const platform = Capacitor.getPlatform();

  async function printReciept(download = false) {
    try {
      setIsLoading(true);
      const prefix = platform == "web" ? "" : "https://backend-new.billzpay.com.ng"

      const response = await axios.get(`${prefix}/api/reciept/${currentTransaction.id}`, {
        withCredentials: true,
        responseType: 'blob'
      })

      const fileName = `receipt-${currentTransaction.id}.pdf`;

      if (download) {
        if (platform === "web") {
          const url = URL.createObjectURL(response.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'receipt.pdf';
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const permission = await Filesystem.requestPermissions();
          if (permission.publicStorage === "granted") {
            const downloadsDir = window.cordova.file.externalRootDirectory + 'Download/';
            const base64Data = await blobToBase64(response.data);

            window.resolveLocalFileSystemURL(downloadsDir, function (dirEntry) {
              dirEntry.getFile(fileName, { create: true }, function (fileEntry) {
                fileEntry.createWriter(function (fileWriter) {
                  fileWriter.onwriteend = function () {
                    const shouldOpen = confirm("Receipt downloaded. Open it now?");
                    if (shouldOpen) {
                      window.cordova.plugins.fileOpener2.open(
                        fileEntry.nativeURL,
                        'application/pdf',
                        {
                          error: function (e) {
                            console.error('Error opening file', e);
                            alert(JSON.stringify(e));
                          },
                          success: function () {
                            console.log('Receipt opened successfully');
                          }
                        }
                      );
                    } else {
                      alert(`Receipt saved to Downloads/${fileName}`);
                    }
                  };
                  fileWriter.onerror = function (e) {
                    console.error('Write failed', e);
                    alert('Failed to save receipt');
                  };
                  fileWriter.write(base64Data);
                }, console.error);
              }, console.error);
            }, console.error);
          } else {
            toast.error("Permission denied. Please allow access to your files to download the receipt.")
          }

        }
      } else {
        const base64Data = await blobToBase64(response.data);
        // alert(base64Data)

        // Save to Filesystem
        await Filesystem.writeFile({
          path: fileName,
          directory: Directory.Temporary,
          data: base64Data,
        });

        // Get file URI and share it
        const uriResult = await Filesystem.getUri({
          path: fileName,
          directory: Directory.Temporary,
        });

        await Share.share({
          title: 'Transaction Receipt',
          text: 'Here is your transaction receipt',
          files: [uriResult.uri]
        });
      }
    } catch (error) {
      console.error('Error handling receipt:', error)
    } finally {
      setIsLoading(false);
    }
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const dataUrl = reader.result;
        const base64 = dataUrl.split(',')[1]; // Strip the data URL prefix
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  }

  return (
    <Dialog
      open={Boolean(currentTransaction)}
      onOpenChange={() => setCurrentTransaction(false)}
    >
      <DialogContent
        className="text-dark font-jakarta dark:bg-darkInput border-0 bg-gray-50 md:w-[400px] lg:w-[400px] dark:text-white"
        id="reciept"
      >
        <div className="mt-4 flex items-center justify-between">
          <div className="flex w-32 cursor-pointer items-center gap-x-1">
            {theme == "dark" ? (
              <img
                src={"/logo-image.svg"}
                alt="logo-symbol"
                className="w-[110px]"
              />
            ) : (
              <img
                src={"/logo-image-black.svg"}
                alt="logo-symbol"
                className="w-[110px]"
              />
            )}
          </div>

          <p className="text-sm font-medium">Transaction Details</p>
        </div>

        <div className="mt-3.5 flex flex-col items-center justify-center gap-2">
          <h2
            className={cn("text-center text-xl leading-3 font-bold", {
              "text-baseColor": currentTransaction?.status
                ?.toLowerCase()
                ?.includes("suc"),
              "text-red-500": !currentTransaction?.status
                ?.toLowerCase()
                ?.includes("suc"),
            })}
          >
            ₦{formatter.format(currentTransaction.amount)}.00
          </h2>

          {currentTransaction?.status?.toLowerCase()?.includes("suc") ? (
            <div className="flex items-center gap-2">
              <p className="text-base">Successful</p>

              <IoShieldCheckmarkOutline />
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <p className="text-base">Failed</p>

              <FcCancel />
            </div>
          )}

          <p className="text-center text-xs">
            {moment(currentTransaction.created_at).format("Do MMM, h:mm:ssA")}
          </p>
        </div>

        <div className="dark:bg-darkBorder h-[0.1px] w-full bg-gray-300"></div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-sm">
            <p className="font-medium text-gray-500 dark:text-gray-200">Type</p>

            <p className="font-medium">
              {currentTransaction.type === ""
                ? "Admin Funding"
                : currentTransaction.type}
            </p>
          </div>

          <div className="flex items-center justify-between text-sm">
            <p className="font-medium text-gray-500 dark:text-gray-200">
              Transaction Number
            </p>

            <p className="font-medium">#{currentTransaction.id}</p>
          </div>

          <div className="flex gap-2 text-sm">
            <p className="font-medium text-gray-500 dark:text-gray-200">
              Details
            </p>

            <p className="flex-1 text-right font-medium">
              {parse(currentTransaction.description || "")}
            </p>
          </div>
        </div>

        <div className="share hidden text-center opacity-70">...</div>

        <div id="controls">
          <div className="flex gap-2">
            <button
              className="bg-primary flex w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-white transition-all disabled:opacity-50"
              onClick={() => printReciept()}
              disabled={isLoading}
            >
              {isLoading ? (
                "Generating Receipt..."
              ) : (
                <>
                  <FaShare size={18} />
                  Share
                </>
              )}
            </button>

          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 text-center text-xs">
          <div className="flex flex-col items-center justify-center gap-2 text-center text-xs">
            <p className="text-center font-medium text-gray-500">
              {currentTransaction?.status?.toLowerCase()?.includes("suc")
                ? "support"
                : "Facing any issues?"}
            </p>

            <p className="text-primary font-medium">support@billzpay.com.ng</p>
            {currentTransaction?.status?.toLowerCase()?.includes("suc") || (
              <>
                <p>Or</p>
                <button
                  className="bg-primary flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs text-white"
                  onClick={() => {
                    window.HubSpotConversations.widget.open();
                  }}
                >
                  <MdSupportAgent size={20} /> Chat with support
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDialog;